import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import {
  useGoogleReviews,
  formatRatingSv,
  formatCountSv,
} from "../lib/googleReviews";

/**
 * Official multi-color Google "G" mark (per Google brand guidelines).
 * Kept inline so the rating banner doesn't depend on a remote asset.
 */
function GoogleG({ className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

/**
 * Reviews banner shown directly under the home page hero.
 * Använder LIVE Google-data från vår server-side proxy.
 * Renderar inget om vi inte har data (så ingen "flash" av tomma siffror).
 */
export default function ReviewsBanner() {
  const google = useGoogleReviews();
  if (!google.hasData) return null;
  return (
    <Link
      to="/#recensioner"
      data-testid="reviews-banner"
      className="group block w-full bg-[#1B2A1E] text-white hover:bg-[#283224] transition-colors"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-3.5 flex flex-wrap items-center justify-center gap-x-3 sm:gap-x-4 gap-y-1.5 text-center sm:text-left">
        <span className="flex items-center gap-2 shrink-0">
          <GoogleG className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
          <span
            className="flex items-center gap-0.5"
            aria-label={`${formatRatingSv(google.rating)} av 5`}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className="w-4 h-4 sm:w-[18px] sm:h-[18px] fill-[#F2A57C] text-[#F2A57C]"
                strokeWidth={1.5}
              />
            ))}
          </span>
        </span>
        <span className="font-heading font-extrabold text-[15px] sm:text-base tracking-tight">
          <span data-testid="reviews-banner-rating">
            {formatRatingSv(google.rating)}/5
          </span>{" "}
          <span
            data-testid="reviews-banner-count"
            className="font-medium text-[#D9DCD3]"
          >
            ({formatCountSv(google.ratingCount)} omdömen)
          </span>
        </span>
        <span
          className="hidden sm:inline h-4 w-px bg-white/25"
          aria-hidden="true"
        />
        <span className="text-[#D9DCD3] text-xs sm:text-sm font-medium">
          Läs vad våra kunder säger om oss
        </span>
        <span className="inline-flex items-center gap-1 text-[#F2A57C] font-semibold text-xs sm:text-sm group-hover:translate-x-0.5 transition-transform">
          Recensioner
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </span>
      </div>
    </Link>
  );
}
